import { Box, Divider, IconButton, Stack, Tooltip } from '@mui/material';
import { LAYOUT_CONSTANTS } from '../../../utils/layout-constants';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';

export interface BottomControlButton {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}

interface BottomControlBarProps {
  // Page-agnostic controls (left side)
  onUndo?: () => void;
  onRedo?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onZoomReset?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;

  // Page-specific controls (right side)
  rightButtons?: BottomControlButton[];

  // Custom right content (for full buttons, etc.)
  rightContent?: React.ReactNode;

  // Additional custom left buttons
  leftButtons?: BottomControlButton[];

  // Center content (action buttons)
  centerContent?: React.ReactNode;

  // Force full-width, ignoring drawer offsets
  fullWidth?: boolean;

  // Drawer state for dynamic positioning
  leftDrawerOpen?: boolean;
  leftDrawerWidth?: number;
  rightDrawerOpen?: boolean;
  rightDrawerWidth?: number;
}

const BottomControlBar = ({
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  canUndo = true,
  canRedo = true,
  rightButtons = [],
  rightContent,
  leftButtons = [],
  centerContent,
  fullWidth = false,
  leftDrawerOpen = false,
  leftDrawerWidth = LAYOUT_CONSTANTS.LEFT_DRAWER_TOTAL_WIDTH(LAYOUT_CONSTANTS.LEFT_DRAWER_DEFAULTS.CODE_EDITOR),
  rightDrawerOpen = false,
  rightDrawerWidth = 400,
}: BottomControlBarProps) => {
  const { t } = useTranslation();

  return (
    <Box
      data-testid="bottom-control-bar"
      sx={(theme) => ({
        position: 'fixed',
        bottom: 0,
        left: fullWidth ? 0 : `var(--left-drawer-offset, ${leftDrawerOpen ? leftDrawerWidth : LAYOUT_CONSTANTS.LEFT_DRAWER_TAB_RAIL_WIDTH}px)`,
        right: fullWidth ? 0 : rightDrawerOpen ? rightDrawerWidth : 0,
        height: 48,
        bgcolor: theme.vars?.palette?.background?.paper,
        borderTop: 1,
        borderColor: theme.vars?.palette?.divider,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        zIndex: theme.zIndex.drawer + 1,
        boxShadow: '0px -2px 8px rgba(0,0,0,0.15)',
        transition: 'var(--bottom-bar-transition, left 0.225s, right 0.225s)',
        willChange: 'left, right',
      })}
    >
      {/* Left side - Page-agnostic controls */}
      <Stack direction="row" spacing={0.5} alignItems="center">
        {onUndo && (
          <Tooltip title={t('bottomControlBar.undo', 'Undo')}>
            <span>
              <IconButton size="small" onClick={onUndo} disabled={!canUndo} aria-label={t('aria-label.undo', 'undo')}>
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {onRedo && (
          <Tooltip title={t('bottomControlBar.redo', 'Redo')}>
            <span>
              <IconButton size="small" onClick={onRedo} disabled={!canRedo} aria-label={t('aria-label.redo', 'redo')}>
                <RedoIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {(onUndo || onRedo) && (onZoomIn || onZoomOut || onZoomReset || leftButtons.length > 0) && (
          <Divider orientation="vertical" flexItem sx={{ mx: 1, height: '60%' }} />
        )}

        {onZoomOut && (
          <Tooltip title={t('bottomControlBar.zoomOut', 'Zoom Out')}>
            <IconButton size="small" onClick={onZoomOut} aria-label={t('aria-label.zoom-out', 'zoom out')}>
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
        )}

        {onZoomIn && (
          <Tooltip title={t('bottomControlBar.zoomIn', 'Zoom In')}>
            <IconButton size="small" onClick={onZoomIn} aria-label={t('aria-label.zoom-in', 'zoom in')}>
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
        )}

        {onZoomReset && (
          <Tooltip title={t('bottomControlBar.zoomReset', 'Reset Zoom')}>
            <IconButton size="small" onClick={onZoomReset} aria-label={t('aria-label.zoom-reset', 'reset zoom')}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}

        {leftButtons.length > 0 && (
          <>
            {(onZoomIn || onZoomOut || onZoomReset) && <Divider orientation="vertical" flexItem sx={{ mx: 1, height: '60%' }} />}
            {leftButtons.map((button, index) => (
              <Tooltip key={index} title={button.label}>
                <span>
                  <IconButton
                    size="small"
                    onClick={button.onClick}
                    disabled={button.disabled}
                    aria-label={button.label}
                    sx={(theme) =>
                      button.active
                        ? {
                            color: theme.vars?.palette?.primary?.main,
                            bgcolor: theme.vars?.palette?.action?.selected,
                          }
                        : {}
                    }
                  >
                    {button.icon}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </>
        )}
      </Stack>

      {/* Center - Action buttons */}
      {centerContent && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {centerContent}
        </Stack>
      )}

      {/* Right side - Page-specific controls */}
      <Stack direction="row" spacing={1} alignItems="center">
        {rightContent}
        {rightButtons.map((button, index) => (
          <Tooltip key={index} title={button.label}>
            <span>
              <IconButton
                size="medium"
                onClick={button.onClick}
                disabled={button.disabled}
                aria-label={button.label}
                sx={(theme) => ({
                  width: 40,
                  height: 40,
                  border: 1,
                  borderColor: button.active ? theme.vars?.palette?.primary?.main : theme.vars?.palette?.divider,
                  bgcolor: button.active ? theme.vars?.palette?.action?.selected : 'transparent',
                  color: button.active ? theme.vars?.palette?.primary?.main : theme.vars?.palette?.text?.primary,
                  '&:hover': {
                    bgcolor: theme.vars?.palette?.action?.hover,
                    borderColor: theme.vars?.palette?.primary?.main,
                  },
                })}
              >
                {button.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );
};

export default BottomControlBar;
